import React, { useState, useEffect, useRef } from 'react';
import {
  Box, TextField, Paper, List, ListItem, ListItemText,
  InputAdornment, Chip, Typography, Divider, Fade, Popper,
  ClickAwayListener, ListItemIcon
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { 
  getAllLocations, 
  getPopularLocations, 
  searchEnhancedLocations,
  jamaicaParishDetails 
} from '../../data/jamaicaLocations';

const JamaicaLocationDropdown = ({ 
  value = '', 
  onChange, 
  placeholder = "Select location in Jamaica",
  label = "Location",
  required = false,
  showPopular = true,
  allowRemote = true,
  error = false,
  helperText = "",
  fullWidth = true,
  sx = {}
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const textFieldRef = useRef(null);
  
  const allLocations = getAllLocations();
  
  useEffect(() => {
    if (searchTerm.length === 0) {
      // Show popular locations when no search term
      if (showPopular) {
        const popular = getPopularLocations();
        setFilteredLocations(popular.slice(0, 8));
      } else {
        setFilteredLocations(allLocations.slice(0, 10));
      }
    } else {
      // Filter based on search term
      const filtered = searchEnhancedLocations(searchTerm, 15);
      setFilteredLocations(filtered);
    }
  }, [searchTerm, showPopular, allLocations]);
  
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);
  
  const handleLocationSelect = (location) => {
    setSearchTerm(location.fullName);
    setIsOpen(false);
    onChange && onChange(location);
  };
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setAnchorEl(textFieldRef.current);
    
    // If user clears the field
    if (value === '') {
      onChange && onChange(null);
    }
  };
  
  const handleInputFocus = () => {
    setIsOpen(true);
    setAnchorEl(textFieldRef.current);
  };
  
  const handleClickAway = () => {
    setIsOpen(false);
  };
  
  const getLocationIcon = (type) => {
    switch (type) {
      case 'parish': return <PublicIcon sx={{ color: '#009639' }} />;
      case 'town': return <LocationOnIcon sx={{ color: '#666' }} />;
      case 'remote': return <HomeWorkIcon sx={{ color: '#ff9800' }} />;
      default: return <BusinessIcon sx={{ color: '#666' }} />;
    }
  };
  
  const getLocationChip = (type) => {
    switch (type) {
      case 'parish': 
        return <Chip label="Parish" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
      case 'town': 
        return <Chip label="Town" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
      case 'remote': 
        return <Chip label="Remote" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.7rem' }} />;
      default: 
        return null;
    }
  };
  
  const groupLocationsByType = (locations) => {
    const grouped = {
      popular: [],
      parishes: [],
      towns: [],
      remote: []
    };
    
    locations.forEach(location => {
      if (jamaicaParishDetails.popularLocations.includes(location.name)) {
        grouped.popular.push(location);
      } else if (location.type === 'parish') {
        grouped.parishes.push(location);
      } else if (location.type === 'town') {
        grouped.towns.push(location);
      } else if (location.type === 'remote') {
        grouped.remote.push(location);
      }
    });
    
    return grouped;
  };
  
  const groupedLocations = groupLocationsByType(filteredLocations);
  
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto', ...sx }}>
        <TextField
          ref={textFieldRef}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon sx={{ color: '#009639' }} />
              </InputAdornment>
            ),
            endAdornment: isOpen && (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#009639'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#009639'
            }
          }}
        />
        
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorEl ? anchorEl.offsetWidth : '100%' }}
        >
          <Fade in={isOpen}>
            <Paper
              elevation={8}
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1
              }}
            >
              {/* Popular Locations */}
              {groupedLocations.popular.length > 0 && searchTerm.length === 0 && (
                <>
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                    <Typography variant="subtitle2" sx={{ color: '#009639', fontWeight: 'bold', fontSize: '0.85rem' }}>
                      🔥 Popular Locations
                    </Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {groupedLocations.popular.map((location, index) => (
                      <ListItem
                        key={`popular-${index}`}
                        button
                        onClick={() => handleLocationSelect(location)}
                        sx={{
                          '&:hover': { backgroundColor: '#e8f5e8' },
                          borderLeft: '3px solid #009639',
                          py: 1
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {getLocationIcon(location.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {location.name}
                              </Typography>
                              <Chip 
                                label="Popular" 
                                size="small" 
                                sx={{ 
                                  backgroundColor: '#009639', 
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 18
                                }} 
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {location.type === 'town' ? `${location.parish} Parish` : location.region}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                </>
              )}
              
              {/* Search Results Header */}
              {searchTerm.length > 0 && (
                <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    Search results for "{searchTerm}"
                  </Typography>
                </Box>
              )}
              
              <List dense sx={{ py: 0 }}>
                {/* Parishes */}
                {groupedLocations.parishes.map((location, index) => (
                  <ListItem
                    key={`parish-${index}`}
                    button
                    onClick={() => handleLocationSelect(location)}
                    sx={{ 
                      '&:hover': { backgroundColor: '#e8f5e8' },
                      py: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      {getLocationIcon(location.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {location.name}
                          </Typography>
                          {getLocationChip(location.type)}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {location.region} County
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                
                {/* Towns */}
                {groupedLocations.towns.map((location, index) => (
                  <ListItem
                    key={`town-${index}`}
                    button
                    onClick={() => handleLocationSelect(location)}
                    sx={{ 
                      '&:hover': { backgroundColor: '#e8f5e8' },
                      py: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      {getLocationIcon(location.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {location.name}
                          </Typography>
                          {getLocationChip(location.type)}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {location.parish} Parish, {location.region}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                
                {/* Remote Options */}
                {allowRemote && groupedLocations.remote.map((location, index) => (
                  <ListItem
                    key={`remote-${index}`}
                    button
                    onClick={() => handleLocationSelect(location)}
                    sx={{ 
                      '&:hover': { backgroundColor: '#fff3e0' },
                      backgroundColor: '#fafafa',
                      py: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      {getLocationIcon(location.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {location.name}
                          </Typography>
                          {getLocationChip(location.type)}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Work from anywhere
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              
              {/* No Results State */}
              {filteredLocations.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No locations found for "{searchTerm}"
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try searching for a parish or major town in Jamaica
                  </Typography>
                </Box>
              )}
              
              {/* Footer Hint */}
              {filteredLocations.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f8f9fa', 
                  borderTop: '1px solid #e9ecef'
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', display: 'block' }}>
                    💡 Tip: Type to search parishes, towns, or select from popular locations
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default JamaicaLocationDropdown;